import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { PincodeProvider } from "./context/PincodeContext";

// Common components and hooks (keep static)
import BottomNav from "./components/common/BottomNav";
import ScrollToTop from "./components/common/ScrollToTop";
import FloatingCartSummary from "./components/common/FloatingCartSummary";
import SideCart from "./components/common/SideCart";
import PincodeModal from "./components/common/PincodeModal";
import usePageTracking from "./hooks/usePageTracking";

// Pages (Lazy)
const Home = lazy(() => import("./pages/Home"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const ProductFeedback = lazy(() => import("./pages/ProductFeedback"));
const ReviewReward = lazy(() => import("./pages/ReviewReward"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const ContactUs = lazy(() => import("./pages/ContactUs"));

// Admin Pages (Lazy)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminProductGroups = lazy(() => import("./pages/admin/AdminProductGroups"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminOrderDetail = lazy(() => import("./pages/admin/AdminOrderDetail"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminOffers = lazy(() => import("./pages/admin/AdminOffers"));
const AdminBanners = lazy(() => import("./pages/admin/AdminBanners"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminInfluencerVideos = lazy(() => import("./pages/admin/AdminInfluencerVideos"));
const AdminReviewReward = lazy(() => import("./pages/admin/AdminReviewReward"));
const AdminFinance = lazy(() => import("./pages/admin/AdminFinance"));


function PageTracker() {
  usePageTracking();
  return null;
}

const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const adminToken = localStorage.getItem("adminToken");
  return adminToken ? children : <Navigate to="/admin/login" />;
};

function App() {
  return (
    <AuthProvider>
      <PincodeProvider>
      <CartProvider>
        <Router>
          <ScrollToTop />
          <PageTracker />
          <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
          <PincodeModal />
          <Suspense fallback={<div className="loader" style={{ margin: '100px auto' }}></div>}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/feedback/:id" element={<ProductFeedback />} />
              <Route path="/review-reward" element={<ReviewReward />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/shipping-policy" element={<ShippingPolicy />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />



              {/* Public/Frictionless Routes */}
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />

              <Route path="/orders" element={<PrivateRoute><Orders /></PrivateRoute>} />
              <Route path="/orders/:id" element={<PrivateRoute><OrderDetail /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
              <Route path="/admin/product-groups" element={<AdminRoute><AdminProductGroups /></AdminRoute>} />
              <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
              <Route path="/admin/orders/:id" element={<AdminRoute><AdminOrderDetail /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/users/:id" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
              <Route path="/admin/offers" element={<AdminRoute><AdminOffers /></AdminRoute>} />
              <Route path="/admin/banners" element={<AdminRoute><AdminBanners /></AdminRoute>} />
              <Route path="/admin/influencers" element={<AdminRoute><AdminInfluencerVideos /></AdminRoute>} />
              <Route path="/admin/categories" element={<AdminRoute><AdminCategories /></AdminRoute>} />
              <Route path="/admin/review-reward" element={<AdminRoute><AdminReviewReward /></AdminRoute>} />
              <Route path="/admin/finance" element={<AdminRoute><AdminFinance /></AdminRoute>} />
            </Routes>
          </Suspense>
          <FloatingCartSummary />
          <SideCart />
          <BottomNav />
        </Router>
      </CartProvider>
      </PincodeProvider>
    </AuthProvider>
  );
}

export default App;
